// React
import { ReactNode } from "react"

// UI
import { PageTemplate, PageTemplateLink } from "@/layouts/PageTemplate"
import { PendingInvestment } from "../investments/components/PendingInvestment"

// Translations
import { useTrans } from "@/i18n"

// Pages
import { Routes } from "@/constants/routes"

// Feature flags
import { useFeatureFlags, useCurrentUserKyc } from "@/context/user"

// GraphQL
import {
	CoreInvestmentTestExperienceChoices,
	RiskKycStateChoices,
} from "@/api/graphql"

// Context
import { useInvestmentTests } from "@/context/investmentTests"

/**
 * InvestorSubMenuTabs
 * @returns
 */
function InvestorSubMenuTabs() {
	const t = useTrans(["investor", "profile"])
	const { getFeatureFlagValue } = useFeatureFlags()

	// Get user info
	const userKyc = useCurrentUserKyc()

	// Context
	const {
		investorProfileExperience,
		investorTestQuestions,
		investorTestRisk,
	} = useInvestmentTests()

	return (
		<>
			<PageTemplateLink
				to={Routes.InvestorProfile}
				data-testid="investor.profile"
				name={t("investor:investor.profile.title")}
				analyticsId="investor.profile"
				badgeNumber={investorProfileExperience ? 0 : 1}
				badge={{
					type: Boolean(investorProfileExperience)
						? "verified"
						: undefined,
				}}
			/>

			{/** Show if user is NOVICE */}
			{investorProfileExperience?.experience ===
				CoreInvestmentTestExperienceChoices.Novice && (
				<>
					<PageTemplateLink
						to={Routes.InvestorTest}
						data-testid="investor.investor_test"
						name={t("investor:investor.test.title")}
						analyticsId="investor.investor_test"
						badgeNumber={investorTestQuestions ? 0 : 1}
						badge={{
							type: Boolean(investorTestQuestions)
								? "verified"
								: undefined,
						}}
					/>
					<PageTemplateLink
						to={Routes.InvestorRisk}
						data-testid="investor.investor_risk"
						name={t("investor:investor.risk.title")}
						analyticsId="investor.investor_risk"
						badgeNumber={investorTestRisk ? 0 : 1}
						badge={{
							type: Boolean(investorTestRisk)
								? "verified"
								: undefined,
						}}
					/>
				</>
			)}

			{getFeatureFlagValue("FEATURE_ENABLE_INVESTOR_FUND_ORIGINS") ===
			true ? (
				<PageTemplateLink
					to={Routes.InvestorFunds}
					data-testid="investor.funds"
					name={t("investor:investor.funds.title")}
					analyticsId="investor.funds"
				/>
			) : null}

			{/** Show these optional items only after required steps are completed */}
			<PageTemplateLink
				to={Routes.InvestorIdentity}
				data-testid="investor.indentity"
				name={t("investor:investor.identity.title")}
				analyticsId="investor.identity"
				badgeNumber={
					userKyc.state === RiskKycStateChoices.VerificationSuccess
						? 0
						: 1
				}
				badge={{
					type:
						userKyc.state ===
						RiskKycStateChoices.VerificationSuccess
							? "verified"
							: undefined,
				}}
			/>
		</>
	)
}

/**
 * InvestorLayout
 * @param param0
 * @returns
 */
export function InvestorLayout({ children }: { children: ReactNode }) {
	const t = useTrans("investor")

	// Return
	return (
		<>
			<title>
				{t("common:common.web.title", {
					page: t("investor:investor.title"),
				})}
			</title>
			<PageTemplate
				title={t("investor:investor.title")}
				tabs={<InvestorSubMenuTabs />}
			>
				<PendingInvestment />

				{children}
			</PageTemplate>
		</>
	)
}
